var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import InfoCardGroupBase from '@widget/list/InfoCardGroup/InfoCardGroupBase';
import ItemCard from '@widget/list/ItemCard';
import Icon from '@icon/Icon';
import Button from '@widget/buttons/Button';
import { useUXPContext } from '@core/context/useUXPContext';
import SearchBox from './SearchBox';
import Checkbox from './Checkbox';
import DataList from '../../list/DataList';
import { hasValue } from '@utils';
const PickerPanel = (p) => {
    const context = useUXPContext();
    const { selectedOptions, selected, options, isDynamicLoading, effectiveFields, valueField, labelField, size, shape, pageSize, placeholder, addButtonLabel, onRemove, onCommit, close } = p;
    const [mode, setMode] = useState('view');
    const [query, setQuery] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    // Add-mode working set — selections are held locally and committed only on "Done".
    const [draft, setDraft] = useState([]);
    const [draftOptions, setDraftOptions] = useState([]);
    const enterAddMode = () => {
        setDraft([...selected]);
        setDraftOptions([...selectedOptions]);
        setQuery('');
        setMode('add');
    };
    const toggleDraft = (opt) => {
        const val = opt === null || opt === void 0 ? void 0 : opt[valueField];
        if (draft.indexOf(val) !== -1) {
            setDraft(draft.filter((v) => v !== val));
            setDraftOptions(draftOptions.filter((o) => (o === null || o === void 0 ? void 0 : o[valueField]) !== val));
        }
        else {
            setDraft([...draft, val]);
            setDraftOptions([...draftOptions, opt]);
        }
    };
    const clearDraft = () => {
        setDraft([]);
        setDraftOptions([]);
    };
    const commitAndClose = () => {
        onCommit(draft, draftOptions);
        close();
    };
    // Static-array options: filter locally as the user types
    useEffect(() => {
        if (isDynamicLoading)
            return;
        const arr = Array.isArray(options) ? options : [];
        if (!hasValue(query)) {
            setFilteredOptions(arr);
            return;
        }
        const q = query.toLowerCase();
        setFilteredOptions(arr.filter((o) => { var _a; return String((_a = o === null || o === void 0 ? void 0 : o[labelField]) !== null && _a !== void 0 ? _a : '').toLowerCase().includes(q); }));
    }, [options, query, labelField, isDynamicLoading]);
    // Dynamic options: pass the search text through to the data function as args
    const loadPaginatedData = useCallback((max, lastToken, args) => {
        return options(max, lastToken, args);
    }, [options]);
    const memorizedArgs = useMemo(() => (hasValue(query) ? { query } : {}), [query]);
    const searchPlaceholder = hasValue(placeholder) ? placeholder : context.$L('uxp-core.text.search');
    const addLabel = hasValue(addButtonLabel)
        ? addButtonLabel
        : (hasValue(placeholder) ? placeholder : context.$L('uxp-core.text.add-locations'));
    const renderCard = (item) => (_jsx(ItemCard, { item: item, imageField: effectiveFields.image, titleField: effectiveFields.title, subTitleField: effectiveFields.subtitle, nameField: effectiveFields.name, size: size, shape: shape }));
    if (mode === 'view') {
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_select__dropdown-list uxpcore_infocardgroup__picker-list", children: [selectedOptions.length === 0 && (_jsx("div", { className: "uxpcore_infocardgroup__empty", children: context.$L('uxp-core.text.no-locations-selected') })), selectedOptions.map((item, index) => (_jsx("div", { className: "uxpcore_select__option uxpcore_infocardgroup__selected-item", children: _jsxs("div", { className: "uxpcore_select__option-content", children: [renderCard(item), _jsx("div", { className: "uxpcore_infocardgroup__remove", onClick: (e) => {
                                            e.stopPropagation();
                                            onRemove(item);
                                        }, title: context.$L('uxp-core.text.remove'), children: _jsx(Icon, { icon: "fas times" }) })] }) }, index)))] }), _jsx("div", { className: "uxpcore_select__dropdown-footer uxpcore_infocardgroup__footer--view", children: _jsx(Button, { variant: "link", leftIcon: "fas plus", title: addLabel, onClick: enterAddMode, className: "uxpcore_infocardgroup__add-link" }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(SearchBox, { value: query, onChange: setQuery, placeholder: searchPlaceholder, autoFocus: true, fullWidth: true, className: "uxpcore_select__search" }), _jsx("div", { className: "uxpcore_select__dropdown-list uxpcore_infocardgroup__picker-list", children: _jsx(DataList, { data: isDynamicLoading ? loadPaginatedData : filteredOptions, pageSize: pageSize || 6, args: memorizedArgs, showFooter: false, renderItem: (opt, key) => {
                        const isSelected = draft.indexOf(opt === null || opt === void 0 ? void 0 : opt[valueField]) !== -1;
                        return (_jsx("div", { className: classNames('uxpcore_select__option', {
                                'uxpcore_select__option--selected': isSelected
                            }), onClick: () => toggleDraft(opt), children: _jsxs("div", { className: "uxpcore_select__option-content", children: [_jsx(Checkbox, { checked: isSelected, onChange: () => { } }), renderCard(opt)] }) }, key));
                    }, renderNoItems: () => (_jsx("div", { className: "uxpcore_infocardgroup__empty", children: context.$L('uxp-core.text.no-matching-items') })) }) }), _jsxs("div", { className: "uxpcore_select__dropdown-footer", children: [_jsx(Button, { title: context.$L('uxp-core.text.clear'), onClick: clearDraft, variant: "secondary", size: "smaller" }), _jsx(Button, { title: context.$L('uxp-core.text.done'), onClick: commitAndClose, size: "smaller" })] })] }));
};
/**
 * @export
 * A form input component for selecting multiple items with InfoCard-style presentation.
 * Similar to MultiSelect but displays selected items as stacked profile images.
 *
 * @example Basic usage with static options:
 * ```
 * const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
 *
 * <InfoCardGroupInput
 *   options={[
 *     { name: 'Team A', id: 'team-a' },
 *     { name: 'Team B', id: 'team-b' }
 *   ]}
 *   selected={selectedTeams}
 *   onChange={(values) => setSelectedTeams(values)}
 *   labelField="name"
 *   valueField="id"
 * />
 * ```
 *
 * @example With custom field mappings:
 * ```
 * <InfoCardGroupInput
 *   options={users}
 *   selected={selectedUserIds}
 *   onChange={setSelectedUserIds}
 *   labelField="fullName"
 *   valueField="userId"
 *   fields={{
 *     image: 'avatar',
 *     name: 'fullName',
 *     title: 'fullName',
 *     subtitle: 'email'
 *   }}
 * />
 * ```
 *
 * @example In DynamicForm:
 * ```
 * {
 *   name: 'teamMembers',
 *   label: 'Team Members',
 *   type: 'json',
 *   renderField: (data, onValueChange) => (
 *     <InfoCardGroupInput
 *       options={availableUsers}
 *       selected={data.teamMembers || []}
 *       onChange={onValueChange}
 *       labelField="name"
 *       valueField="id"
 *     />
 *   )
 * }
 * ```
 *
 * @category Inputs/Selection
 */
const InfoCardGroupInput = (props) => {
    const context = useUXPContext();
    const { options, selected = [], onChange, labelField = 'label', valueField = 'value', fields, placeholder, className, isValid, maxVisible = 5, size = 'medium', shape = 'circle', dropdownPosition = 'bottom-left', addButtonLabel, pageSize, selectedLabels } = props;
    const [selectedOptions, setSelectedOptions] = useState([]);
    const isDynamicLoading = typeof options === 'function';
    // Build default fields mapping if not provided
    const effectiveFields = useMemo(() => {
        if (fields)
            return fields;
        // Default: use labelField for name/title
        return {
            name: labelField,
            title: labelField,
            subtitle: undefined,
            image: undefined
        };
    }, [fields, labelField]);
    // Update selected options when selected values or options change
    useEffect(() => {
        updateSelectedOptions();
    }, [selected, options, selectedLabels]);
    function updateSelectedOptions() {
        return __awaiter(this, void 0, void 0, function* () {
            let selOpts = [];
            if (selected && selected.length > 0) {
                if (Array.isArray(options)) {
                    selOpts = options.filter((option) => (!!option && selected.indexOf(option[valueField]) !== -1));
                }
                else if (!!selectedLabels) {
                    if (Array.isArray(selectedLabels)) {
                        selOpts = selected.map((val, index) => ({
                            [labelField]: selectedLabels[index] || val,
                            [valueField]: val
                        }));
                    }
                    else if (typeof selectedLabels === 'function') {
                        try {
                            const opts = yield selectedLabels(selected);
                            if (Array.isArray(opts) && opts.length > 0) {
                                selOpts = opts;
                            }
                        }
                        catch (e) {
                            console.error('Unable to get the selected options details. error:', e);
                        }
                    }
                }
            }
            setSelectedOptions(selOpts);
        });
    }
    // Remove a single committed item (View mode) — immediate.
    const handleRemoveItem = (item) => {
        const itemValue = item[valueField];
        const newValues = selected.filter(v => v !== itemValue);
        const newOptions = selectedOptions.filter(opt => opt[valueField] !== itemValue);
        onChange(newValues, newOptions);
    };
    return (_jsx(InfoCardGroupBase, { items: selectedOptions, fields: effectiveFields, maxVisible: maxVisible, size: size, shape: shape, dropdownPosition: dropdownPosition, className: className, dropdownContentClassName: "uxpcore_select__dropdown uxpcore_select__dropdown--multi uxpcore_infocardgroup__picker", dropdownMinWidth: "20rem", dropdownMaxWidth: "24rem", dropdownContent: ({ close }) => (_jsx(PickerPanel, { selectedOptions: selectedOptions, selected: selected, options: options, isDynamicLoading: isDynamicLoading, effectiveFields: effectiveFields, valueField: valueField, labelField: labelField, size: size, shape: shape, pageSize: pageSize, placeholder: placeholder, addButtonLabel: addButtonLabel, onRemove: handleRemoveItem, onCommit: onChange, close: close })), enableAddButton: true }));
};
export default InfoCardGroupInput;
